import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/hooks/use-toast";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Link } from "react-router-dom";

// Helper function to get CSRF token from meta tags
const getCsrfToken = () => {
    const csrfTokenTag = document.querySelector("meta[name='csrf-token']");
    const csrfParamTag = document.querySelector("meta[name='csrf-param']");

    if (!csrfTokenTag || !csrfParamTag) {
        console.error("CSRF token vagy param meta tag nem található!");
        return { token: null, param: null };
    }

    return {
        token: csrfTokenTag.getAttribute("content"),
        param: csrfParamTag.getAttribute("content"),
    };
};

interface NamesCleanerPageProps {
    gameId?: string;
    versionId?: string;
}

const NamesCleanerPage: React.FC<NamesCleanerPageProps> = ({ gameId: propGameId, versionId: propVersionId }) => {
    const { gameId: paramGameId, versionId: paramVersionId } = useParams();
    const gameId = propGameId || paramGameId;
    const versionId = propVersionId || paramVersionId;

    const [searchTerm, setSearchTerm] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [searchType, setSearchType] = useState<"content" | "name">("content");
    const [activeSearchType, setActiveSearchType] = useState<"content" | "name">("content");
    const [hideTranslated, setHideTranslated] = useState(false);
    const [caseSensitive, setCaseSensitive] = useState(true);
    const [sortBy, setSortBy] = useState<"size" | "relevance">("relevance");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['listaSearch', gameId, searchQuery, activeSearchType, hideTranslated, caseSensitive, sortBy, sortOrder],
        queryFn: async () => {
            if (!searchQuery) return { results: [], count: 0 };
            const response = await fetch(`/api/games/${gameId}/lista_search?q=${encodeURIComponent(searchQuery)}&type=${activeSearchType}&hide_translated=${hideTranslated}&case_sensitive=${caseSensitive}&sort_by=${sortBy}&sort_order=${sortOrder}`);
            if (!response.ok) throw new Error('Keresés sikertelen');
            return response.json();
        },
        enabled: !!gameId && !!searchQuery,
    });

    // Elfogadás mutation
    const acceptMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");
            const response = await fetch(`/api/extracted_texts/${translationId}/accept_translation`, {
                method: "PATCH",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Elfogadás sikertelen");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Sikeres!", description: "Fordítás elfogadva." });
            refetch();
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    // Visszavonás mutation
    const revertMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");
            const response = await fetch(`/api/extracted_texts/${translationId}/revert_acceptance`, {
                method: "PATCH",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Visszavonás sikertelen");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Sikeres!", description: "Elfogadás visszavonva." });
            refetch();
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    // Törlés mutation
    const deleteMutation = useMutation({
        mutationFn: async (translationId: number) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");
            const response = await fetch(`/api/extracted_texts/${translationId}`, {
                method: "DELETE",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Törlés sikertelen");
            }
            // A DELETE 204 No Content választ ad, ami nem JSON
            return { success: true };
        },
        onSuccess: () => {
            toast({ title: "Sikeres!", description: "Fordítás törölve." });
            refetch();
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    // Eredetit alkalmaz mutation - törli a HU fordításokat és az EN content-et állítja be elfogadottként
    const applyOriginalMutation = useMutation({
        mutationFn: async (sourceTextId: number) => {
            const { token } = getCsrfToken();
            if (!token) throw new Error("CSRF token nem található.");
            const response = await fetch(`/api/games/${gameId}/lista_search/apply_original?id=${sourceTextId}`, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": token,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Művelet sikertelen");
            }
            return response.json();
        },
        onSuccess: () => {
            toast({ title: "Sikeres!", description: "Eredeti szöveg alkalmazva fordításként." });
            refetch();
        },
        onError: (error) => {
            toast({ title: "Hiba", description: error.message, variant: "destructive" });
        }
    });

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            setSearchQuery(searchTerm);
            setActiveSearchType(searchType);
        }
    };

    const handleSearch = () => {
        setSearchQuery(searchTerm);
        setActiveSearchType(searchType);
    };

    return (
        <div className="p-8">
            <div className="space-y-4">
                {/* Fejléc */}
                <div>
                    <h2 className="text-2xl font-bold">Lista</h2>
                    <p className="text-muted-foreground">Szövegek keresése és listázása</p>
                </div>

                {/* Kereső sáv */}
                <div className="flex gap-2 items-center">
                    <Input
                        placeholder={searchType === "content" ? "Keresés angol szövegben..." : "Keresés fájlnévben..."}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="flex-1"
                    />
                    <div className="flex items-center gap-3 text-sm">
                        <label className="flex items-center gap-1 cursor-pointer">
                            <input
                                type="radio"
                                name="searchType"
                                checked={searchType === "content"}
                                onChange={() => setSearchType("content")}
                                className="w-4 h-4"
                            />
                            <span>Szövegben</span>
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                            <input
                                type="radio"
                                name="searchType"
                                checked={searchType === "name"}
                                onChange={() => setSearchType("name")}
                                className="w-4 h-4"
                            />
                            <span>Fájlnév</span>
                        </label>
                    </div>
                    <Button onClick={handleSearch}>Keresés</Button>
                </div>

                {/* Szűrők */}
                <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <Checkbox
                            checked={hideTranslated}
                            onCheckedChange={(checked) => setHideTranslated(checked === true)}
                        />
                        <span>Lefordítottak elrejtése</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <Checkbox
                            checked={caseSensitive}
                            onCheckedChange={(checked) => setCaseSensitive(checked === true)}
                        />
                        <span>Kis/nagybetű érzékenység</span>
                    </label>
                </div>

                {/* Rendezés */}
                {data && data.count > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                        <span>Rendezés:</span>
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as "size" | "relevance")}
                            className="px-2 py-1 border rounded"
                        >
                            <option value="relevance">Relevancia</option>
                            <option value="size">Szöveg mérete</option>
                        </select>
                        {sortBy === "size" && (
                            <button
                                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                                className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200"
                            >
                                {sortOrder === "asc" ? "↑ Növekvő" : "↓ Csökkenő"}
                            </button>
                        )}
                    </div>
                )}

                {/* Eredmények */}
                {isLoading && <p>Keresés...</p>}
                {isError && <p className="text-red-500">Hiba: {error?.message}</p>}

                {!searchQuery && (
                    <p className="text-muted-foreground text-sm">Írd be a keresőkifejezést és nyomj Entert a kereséshez.</p>
                )}

                {data && data.count > 0 && (
                    <div className="space-y-2">
                        <p className="text-base font-semibold">Találat: {data.count} szöveg</p>
                        <Table>
                            <TableHeader className="sticky top-0 bg-background">
                                <TableRow>
                                    <TableHead className="min-w-[300px] text-base">Angol</TableHead>
                                    <TableHead className="min-w-[300px] text-base">Javasolt fordítás(ok)</TableHead>
                                    <TableHead className="w-32 text-center text-base">Műveletek</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.results.map((item: any) => (
                                    <TableRow key={item.id} className="hover:bg-muted/50">
                                        <TableCell>
                                            <div className="break-words max-w-[400px]">
                                                <Link
                                                    to={`/games/${gameId}/versions/${versionId}/translate?highlight_id=${item.id}`}
                                                    className="font-mono text-sm text-blue-500 hover:underline"
                                                    title="Ugrás a fordításhoz"
                                                >
                                                    [{item.name}]
                                                </Link>
                                                <br />
                                                <span className="text-base">{item.content}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="space-y-2">
                                                {/* Elfogadott fordítás */}
                                                {item.accepted_translation && (
                                                    <div className="flex items-center gap-2 text-green-600 flex-wrap">
                                                        <span className="font-semibold text-lg">✓</span>
                                                        <span className="break-all text-base">{item.accepted_translation.content}</span>
                                                        <span className="text-muted-foreground text-sm">({item.accepted_translation.user_username})</span>
                                                        <div className="flex gap-1 ml-auto">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-6 px-2 text-orange-500 hover:text-orange-700"
                                                                onClick={() => revertMutation.mutate(item.accepted_translation.id)}
                                                                disabled={revertMutation.isPending}
                                                                title="Visszavon"
                                                            >
                                                                ↩️
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-6 px-2 text-red-500 hover:text-red-700"
                                                                onClick={() => {
                                                                    if (window.confirm("Biztosan törölni szeretnéd ezt a fordítást?")) {
                                                                        deleteMutation.mutate(item.accepted_translation.id);
                                                                    }
                                                                }}
                                                                disabled={deleteMutation.isPending}
                                                                title="Törlés"
                                                            >
                                                                🗑️
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                                {/* Pending fordítások */}
                                                {item.pending_translations.map((trans: any) => (
                                                    <div key={trans.id} className="flex items-center gap-2 text-yellow-600 flex-wrap">
                                                        <span className="text-lg">⏳</span>
                                                        <span className="break-all text-base">{trans.content}</span>
                                                        <span className="text-muted-foreground text-sm">({trans.user_username})</span>
                                                        <div className="flex gap-1 ml-auto">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-6 px-2 text-green-500 hover:text-green-700"
                                                                onClick={() => acceptMutation.mutate(trans.id)}
                                                                disabled={acceptMutation.isPending}
                                                                title="Elfogad"
                                                            >
                                                                ✓
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-6 px-2 text-red-500 hover:text-red-700"
                                                                onClick={() => {
                                                                    if (window.confirm("Biztosan törölni szeretnéd ezt a fordítást?")) {
                                                                        deleteMutation.mutate(trans.id);
                                                                    }
                                                                }}
                                                                disabled={deleteMutation.isPending}
                                                                title="Törlés"
                                                            >
                                                                🗑️
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                                {!item.accepted_translation && item.pending_translations.length === 0 && (
                                                    <span className="text-muted-foreground italic">Nincs fordítás</span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <div className="flex flex-col gap-1">
                                                <a
                                                    href={`/games/${gameId}/versions/${versionId}/translate?highlight_id=${item.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center justify-center h-8 px-3 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                                                    title="Ugrás a fordításhoz (új ablakban)"
                                                >
                                                    🔗 Megnyit
                                                </a>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="h-8 px-3 text-xs text-orange-600 border-orange-300 hover:bg-orange-50"
                                                    onClick={() => applyOriginalMutation.mutate(item.id)}
                                                    disabled={applyOriginalMutation.isPending}
                                                    title="Törli a HU fordításokat és az eredeti EN szöveget állítja be elfogadott fordításként"
                                                >
                                                    📝 Eredetit alkalmaz
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}

                {data && data.count === 0 && searchQuery && (
                    <p className="text-muted-foreground">Nincs találat a(z) "{searchQuery}" kifejezésre.</p>
                )}
            </div>
        </div>
    );
};

export default NamesCleanerPage;
